/**
 * iter101.3 — Impersonation landing page.
 *
 * The tab is opened SYNCHRONOUSLY (from the admin's click on the
 * Impersonate button) at `/impersonate-land?loading=1` so the browser
 * popup blocker doesn't fire. This component then:
 *   1. Sends a "ready" ping to `window.opener` once its listener is
 *      attached (handshake — prevents the classic postMessage race
 *      where the parent fires before the child's listener is up).
 *   2. Listens for the opener's reply with the impersonation token.
 *   3. Writes the JWT to sessionStorage.ca_token (per-tab, isolated
 *      from the admin's localStorage.ca_token).
 *   4. Navigates to the target portal (e.g. /doctor, /patient).
 *
 * Both `@/lib/api` and `@/context/AuthContext` read sessionStorage
 * first (see `readAuthToken` there), so this tab operates entirely
 * under the impersonated identity while the admin's other tab keeps
 * its own localStorage token.
 *
 * Legacy: for backwards compat and manual link-based flows the page
 * also accepts `?token=...&redirect=...` on the URL. In practice the
 * handshake path is primary — URL-based tokens leave the JWT in
 * browser history which we'd rather avoid.
 */
import React, { useEffect, useState } from "react";

const MESSAGE_TYPE_READY = "invisique:impersonate:ready";
const MESSAGE_TYPE_TOKEN = "invisique:impersonate:token";

export default function ImpersonationLand() {
    const [error, setError] = useState("");
    const [state, setState] = useState("loading"); // "loading" | "signing_in" | "error"

    useEffect(() => {
        const applyToken = (token, redirect, label, role) => {
            try {
                sessionStorage.setItem("ca_token", token);
                if (label) sessionStorage.setItem("ca_imp_label", label);
                if (role) sessionStorage.setItem("ca_imp_role", role);
                sessionStorage.setItem("ca_imp_active", "1");
                setState("signing_in");
                // Full page replace so React re-mounts under the new identity.
                window.location.replace(redirect || "/");
            } catch (e) {
                setState("error");
                setError(String(e?.message || e) || "Could not start impersonated session.");
            }
        };

        // Legacy URL-token path (still supported for direct links).
        try {
            const params = new URLSearchParams(window.location.search);
            const urlToken = params.get("token");
            if (urlToken) {
                const redirect = params.get("redirect") || "/";
                const label = params.get("label") || "";
                const role = params.get("role") || "";
                window.history.replaceState({}, document.title, "/impersonate-land");
                applyToken(urlToken, redirect, label, role);
                return undefined; // no listener installed
            }
        } catch { /* fall through to handshake path */ }

        // Primary path: handshake with window.opener.
        const onMessage = (ev) => {
            if (ev.origin !== window.location.origin) return;
            const d = ev.data || {};
            if (d.type !== MESSAGE_TYPE_TOKEN) return;
            if (d.error) {
                setState("error");
                setError(d.error);
                return;
            }
            if (!d.token) return;
            applyToken(d.token, d.redirect || "/", d.label || "", d.role || "");
        };
        window.addEventListener("message", onMessage);

        // Send the "ready" ping. Once opener sees this it replies
        // with the token payload. If opener doesn't exist (e.g. the
        // user opened this URL directly), we'll timeout below.
        const pingOpener = () => {
            try {
                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage(
                        { type: MESSAGE_TYPE_READY },
                        window.location.origin,
                    );
                }
            } catch { /* cross-origin — safe to ignore */ }
        };
        // Ping immediately + a couple of retries in case opener's
        // own listener wasn't attached yet when we mounted.
        pingOpener();
        const t1 = setTimeout(pingOpener, 250);
        const t2 = setTimeout(pingOpener, 750);

        // Safety net — if nothing arrives after 20s, show a helpful
        // error so the user doesn't stare at a spinner forever.
        const timeout = setTimeout(() => {
            setState("error");
            setError("Impersonation handoff timed out — close this tab and try again from the admin console.");
        }, 20000);

        return () => {
            window.removeEventListener("message", onMessage);
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(timeout);
        };
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="impersonate-land">
            <div className="max-w-sm text-center px-8">
                {state === "error" ? (
                    <>
                        <div className="text-sm font-semibold text-rose-700" data-testid="impersonate-land-error">Couldn&apos;t sign in as this user</div>
                        <div className="text-xs text-muted-foreground mt-2">{error}</div>
                    </>
                ) : state === "signing_in" ? (
                    <>
                        <div className="text-sm font-semibold" data-testid="impersonate-land-signing-in">Opening portal…</div>
                        <div className="text-xs text-muted-foreground mt-1">Signing you in as the selected user in this tab.</div>
                    </>
                ) : (
                    <>
                        <div className="inline-block w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin mb-3" />
                        <div className="text-sm font-semibold" data-testid="impersonate-land-loading">Preparing impersonated session…</div>
                        <div className="text-xs text-muted-foreground mt-1">Waiting for the admin console to hand over the login token.</div>
                    </>
                )}
            </div>
        </div>
    );
}
